import React, { useState, useEffect } from "react";
import { Modal, Button, Nav } from "react-bootstrap";
import Swal from "sweetalert2";

import { APP_INFO } from "../../constants/AppInfo";
import "./LoginModal.css";

import AuthService from "../../services/AuthService";
import storage from "../../utils/storage";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const LoginModal = ({ show, onHide }) => {
  const [loading, setLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("login");

  // Login
  const [mobile, setMobile] = useState("");
  const [loginForm, setLoginForm] = useState(true);

  // Sign Up
  const [signupForm, setSignupForm] = useState(true);
  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    mobile: "",
  });

  // OTP
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [token, setToken] = useState("");
  const [timer, setTimer] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  // ---------------- LOGIN FLOW ----------------
  const handleSendOTP = async () => {
    if (mobile.length !== 10) {
      Swal.fire(
        "Opps!",
        "Please enter a valid 10-digit mobile number.",
        "error",
      );
      return;
    }
    setLoading(true);

    try {
      const result = await AuthService.loginWithMobile(mobile);

      if (!result.success) {
        Swal.fire("Opps!", result.message, "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        setToken(responseData.data.token);
        setLoginForm(false);
      } else if (responseData.message === "USER_DOES_NOT_EXITS") {
        // Auto switch to Sign Up tab
        Swal.fire("Info", "User not registered. Please sign up first.", "info");
        // setActiveTab("signup");
      } else {
        Swal.fire("Opps!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Opps!", error.response?.data || error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async () => {
    const combinedOtp = otp.join("");
    if (combinedOtp.length !== 6) {
      Swal.fire("Opps!", "Please enter a complete 6-digit OTP", "error");
      return;
    }

    setLoading(true);

    try {
      const result = await AuthService.verifyOtp(combinedOtp, token);
      if (!result.success) {
        Swal.fire("Opps!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        var data = responseData.data;

        storage.setItem("access_token", data.access_token);
        storage.setItem("user_id", data.user_id);
        storage.setItem("mobile", data.mobile);
        storage.setItem("user_type", data.user_type);
        storage.setItem("name", data.name);
        storage.setItem("state", data?.state?.toUpperCase() || "");
        storage.setItem(
          "premium_subscription_status",
          data.premium_subscription_status,
        );

        onHide();
        window.location.reload();
      } else {
        Swal.fire("Opps!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Opps!", error.response?.data || error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- SIGN UP FLOW ----------------
  const handleSignupSendOTP = async () => {
    const { name, email, mobile } = signupData;

    if (!name.trim()) {
      Swal.fire("Oops!", "Name is required.", "error");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      Swal.fire("Oops!", "Enter a valid email address.", "error");
      return;
    }
    if (mobile.length !== 10) {
      Swal.fire("Oops!", "Enter a valid 10-digit mobile number.", "error");
      return;
    }

    setLoading(true);
    try {
      const result = await AuthService.signupSendOtp(
        name,
        email,
        mobile,
        "",
        "",
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setToken(responseData.data.token);
        setSignupForm(false);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", error.response?.data || error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupOtpVerify = async () => {
    const combinedOtp = otp.join("");
    if (combinedOtp.length !== 6) {
      Swal.fire("Oops!", "Please enter a complete 6-digit OTP.", "error");
      return;
    }

    setLoading(true);
    try {
      const result = await AuthService.signupVerifyOtp(combinedOtp, token);

      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        var data = responseData.data;

        storage.setItem("access_token", data.access_token);
        storage.setItem("user_id", data.user_id);
        storage.setItem("mobile", data.mobile);
        storage.setItem("user_type", data.user_type);
        storage.setItem("name", data.name);

        onHide();
        window.location.reload();
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", error.response?.data || error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- OTP INPUT ----------------
  const handleOtpChange = (index, value) => {
    if (/^\d*$/.test(value) && value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };

  // ---------------- TIMER ----------------
  useEffect(() => {
    let interval;
    if (timer > 0 && isResendDisabled) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer, isResendDisabled]);

  const handleResendOtp = () => {
    setTimer(30);
    setIsResendDisabled(true);
    setOtp(["", "", "", "", "", ""]);
    document.getElementById("otp-input-0").focus();
  };

  return (
    <React.Fragment>
      {loading && <LoadingScreen />}

      <Modal
        show={show}
        onHide={onHide}
        centered
        size="xl"
        className="login-modal"
      >
        <Modal.Body className="p-0">
          <div className="login-modal-content">
            {/* Close */}
            <Button
              variant="link"
              className="p-0 position-absolute top-0 end-0 m-3"
              onClick={onHide}
            >
              <i className="fas fa-times fs-4"></i>
            </Button>

            <div
              className="d-flex flex-column flex-md-row"
              style={{ height: "75vh" }}
            >
              {/* Left Panel - QR Section (hidden on mobile) */}
              <div className="w-100 w-md-50 bg-light d-none d-md-flex flex-column align-items-center justify-content-center p-4">
                <img
                  src="/assets/images/logo.png"
                  alt="Logo"
                  style={{ height: "60px", marginBottom: "20px" }}
                />

                <h5 className="fw-bold text-center mb-2">
                  Welcome to {APP_INFO.companyName}
                </h5>
                <p className="text-center small mb-3 px-3">
                  Powering recharges, bills, and digital payments nationwide.
                </p>

                {/* QR Code */}
                <div className="qr-wrapper shadow mb-2">
                  <img
                    src="/assets/images/qr-code.png"
                    alt="QR Code"
                    className="img-fluid"
                    width={200}
                  />
                </div>
                <p className="text-center small mb-3">
                  <strong>Scan to download our app</strong>
                  <br />
                  For smooth & fast experience
                </p>

                <a
                  href="https://play.google.com/store"
                  className="btn btn-dark w-100 d-flex align-items-center justify-content-center gap-2 mb-3"
                >
                  Download App
                </a>

                {/* Feature Icons */}
                <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
                  <div className="text-center">
                    <img
                      src="/assets/images/secured-icon.png"
                      alt="Secure"
                      className="feature-icon"
                    />
                    <div className="small">Safe and Secured</div>
                  </div>
                  <div className="text-center">
                    <img
                      src="/assets/images/rupee-icon.png"
                      alt="Cashback"
                      className="feature-icon"
                    />
                    <div className="small">Realtime Cashback</div>
                  </div>
                  <div className="text-center">
                    <img
                      src="/assets/images/happy-user-icon.png"
                      alt="Transactions"
                      className="feature-icon"
                    />
                    <div className="small">Join 10 Lakh+ Happy Users</div>
                  </div>
                </div>
              </div>

              {/* Right Panel */}
              <div className="w-100 w-md-50 d-flex flex-column justify-content-center p-4 overflow-auto">
                {/* Tabs */}
                <Nav
                  variant="tabs"
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                >
                  <Nav.Item>
                    <Nav.Link eventKey="login">Login</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="signup">Sign Up</Nav.Link>
                  </Nav.Item>
                </Nav>

                {/* LOGIN TAB */}
                {activeTab === "login" && (
                  <>
                    {loginForm ? (
                      <>
                        <h5 className="fw-bold mb-2">Login</h5>
                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Enter 10-digit mobile number"
                          value={mobile}
                          onChange={(e) =>
                            setMobile(e.target.value.replace(/\D/g, ""))
                          }
                          maxLength="10"
                        />
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={handleSendOTP}
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Send OTP"}
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex flex-column align-items-center">
                        <h4 className="fw-bold mb-2 text-center">Verify OTP</h4>
                        <p className="text-muted">
                          Enter the 6-digit OTP sent to{" "}
                          <span className="fw-bold">+91 {mobile}</span>
                        </p>
                        <div className="d-flex justify-content-center gap-2 mb-4">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              id={`otp-input-${index}`}
                              type="text"
                              className="form-control text-center otp-input"
                              style={{
                                width: "45px",
                                height: "50px",
                                fontSize: "1.2rem",
                              }}
                              value={digit}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              maxLength={1}
                              autoFocus={index === 0}
                            />
                          ))}
                        </div>
                        <Button
                          variant="primary"
                          className="w-100 py-2 mb-3"
                          onClick={handleOtpVerify}
                        >
                          Verify OTP
                        </Button>
                        <p className="text-muted text-center">
                          Didn't receive OTP?{" "}
                          {isResendDisabled ? (
                            <span className="fw-bold">Resend in {timer}s</span>
                          ) : (
                            <button
                              className="btn btn-link p-0 text-decoration-none fw-bold"
                              onClick={handleResendOtp}
                            >
                              Resend OTP
                            </button>
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* SIGN UP TAB */}
                {activeTab === "signup" && (
                  <>
                    {signupForm ? (
                      <>
                        <h5 className="fw-bold mb-2">Sign Up</h5>
                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Full Name"
                          value={signupData.name}
                          onChange={(e) =>
                            setSignupData({
                              ...signupData,
                              name: e.target.value,
                            })
                          }
                        />
                        <input
                          type="email"
                          className="form-control mb-3"
                          placeholder="Email Address"
                          value={signupData.email}
                          onChange={(e) =>
                            setSignupData({
                              ...signupData,
                              email: e.target.value,
                            })
                          }
                        />
                        <input
                          type="text"
                          className="form-control mb-3"
                          placeholder="Enter 10-digit mobile number"
                          value={signupData.mobile}
                          onChange={(e) =>
                            setSignupData({
                              ...signupData,
                              mobile: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          maxLength="10"
                        />
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={handleSignupSendOTP}
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Send OTP"}
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex flex-column align-items-center">
                        <h4 className="fw-bold mb-2 text-center">Verify OTP</h4>
                        <p className="text-muted">
                          Enter the 6-digit OTP sent to{" "}
                          <span className="fw-bold">
                            +91 {signupData.mobile}
                          </span>
                        </p>
                        <div className="d-flex justify-content-center gap-2 mb-4">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              id={`otp-input-${index}`}
                              type="text"
                              className="form-control text-center otp-input"
                              style={{
                                width: "45px",
                                height: "50px",
                                fontSize: "1.2rem",
                              }}
                              value={digit}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              maxLength={1}
                              autoFocus={index === 0}
                            />
                          ))}
                        </div>
                        <Button
                          variant="primary"
                          className="w-100 py-2 mb-3"
                          onClick={handleSignupOtpVerify}
                        >
                          Verify OTP
                        </Button>
                        <p className="text-muted text-center">
                          Didn't receive OTP?{" "}
                          {isResendDisabled ? (
                            <span className="fw-bold">Resend in {timer}s</span>
                          ) : (
                            <button
                              className="btn btn-link p-0 text-decoration-none fw-bold"
                              onClick={handleResendOtp}
                            >
                              Resend OTP
                            </button>
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                <p className="text-center small text-muted mt-3">
                  By continuing you agree to our{" "}
                  <a href="/terms-and-conditions">Terms & Conditions</a> and{" "}
                  <a href="/privacy-policy">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default LoginModal;
